import React, { useState, useRef, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Avatar, Button } from '@common/musicHubCommon';
import { ROUTES } from '../../routes';
import { useUserStore, useGenreStore, useMoodStore } from '../../store';
import './index.scss';

// Logo 组件
const Logo: React.FC = () => (
  <div className="sidebar__logo">
    <img src="https://akoss.bangcdn.net/fe/leftLogo.svg" alt="Phoenix Music" />
  </div>
);

// 侧边栏导航项
interface NavItemProps {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  onClick?: () => void;
}

const NavItem: React.FC<NavItemProps> = ({ icon, label, active, onClick }) => (
  <div 
    className={`sidebar__nav-item ${active ? 'sidebar__nav-item--active' : ''}`}
    onClick={onClick}
  >
    <span className="sidebar__nav-icon">{icon}</span>
    <span className="sidebar__nav-label">{label}</span>
  </div>
);

// 图标组件
const HOME_ICON = 'https://akoss.bangcdn.net/fe/home.svg';

const MY_SONGS_ICON = 'https://akoss.bangcdn.net/fe/my%20songs.svg';

const UPLOAD_ICON = 'https://akoss.bangcdn.net/fe/Upload.svg';

const LogoutIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/>
  </svg>
);

// 用户头像下拉菜单
interface UserAvatarDropdownProps {
  user: any;
  onNavigate: (path: string) => void;
  onLogout: () => void;
}

const UserAvatarDropdown: React.FC<UserAvatarDropdownProps> = ({ user, onNavigate, onLogout }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="user-avatar-dropdown" ref={dropdownRef}>
      <div 
        className="user-avatar-dropdown__trigger"
        onClick={() => setIsOpen(!isOpen)}
      >
        <Avatar src={user?.avatar} size="medium" />
      </div>
      {isOpen && (
        <div className="user-avatar-dropdown__menu">
          <div className="user-avatar-dropdown__header">
            {user?.name || 'Artist Name'}
          </div>
          <div className="user-avatar-dropdown__divider" />
          <div 
            className="user-avatar-dropdown__item"
            onClick={() => { onNavigate(ROUTES.dashboard); setIsOpen(false); }}
          >
            <img src={HOME_ICON} alt="" width={24} height={24} />
            <span>Home</span>
          </div>
          {/* <div 
            className="user-avatar-dropdown__item"
            onClick={() => { onNavigate(ROUTES.editProfile); setIsOpen(false); }}
          >
            <SettingsIcon />
            <span>Edit Profile</span>
          </div> */}
          <div className="user-avatar-dropdown__divider" />
          <div 
            className="user-avatar-dropdown__item"
            onClick={() => { onLogout(); setIsOpen(false); }}
          >
            <LogoutIcon />
            <span>Sign out</span>
          </div>
        </div>
      )}
    </div>
  );
};

// 移动端顶部导航
interface MobileHeaderProps {
  user: any;
  onNavigate: (path: string) => void;
  onLogout: () => void;
}

const MobileHeader: React.FC<MobileHeaderProps> = ({ user, onNavigate, onLogout }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="mobile-header">
      <div className="mobile-header__logo">
        <span className="mobile-header__logo-main">Phoenix Music</span>
        <span className="mobile-header__logo-sub">FOR ARTISTS</span>
      </div>
      <div className="mobile-header__avatar-wrapper" ref={dropdownRef}>
        <div className="mobile-header__avatar" onClick={() => setIsOpen(!isOpen)}>
          <Avatar src={user?.avatar} size="medium" />
        </div>
        {isOpen && (
          <div className="mobile-header__dropdown">
            <div className="mobile-header__dropdown-header">
              {user?.name || 'Artist Name'}
            </div>
            <div className="mobile-header__dropdown-divider" />
            <div 
              className="mobile-header__dropdown-item"
              onClick={() => { onNavigate(ROUTES.dashboard); setIsOpen(false); }}
            >
              <img src={HOME_ICON} alt="" width={24} height={24} />
              <span>Home</span>
            </div>
            {/* <div 
              className="mobile-header__dropdown-item"
              onClick={() => { onNavigate(ROUTES.editProfile); setIsOpen(false); }}
            >
              <SettingsIcon />
              <span>Edit Profile</span>
            </div> */}
            <div className="mobile-header__dropdown-divider" />
            <div 
              className="mobile-header__dropdown-item"
              onClick={() => { onLogout(); setIsOpen(false); }}
            >
              <LogoutIcon />
              <span>Sign out</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// 移动端底部导航
interface MobileTabBarProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
}

const MobileTabBar: React.FC<MobileTabBarProps> = ({ activeTab, onTabChange }) => (
  <div className="mobile-tab-bar">
    <div 
      className={`mobile-tab-bar__item ${activeTab === 'dashboard' ? 'mobile-tab-bar__item--active' : ''}`}
      onClick={() => onTabChange('dashboard')}
    >
      <img src={HOME_ICON} alt="" width={24} height={24} />
      <span>Dashboard</span>
    </div>
    <div 
      className={`mobile-tab-bar__item ${activeTab === 'my-songs' ? 'mobile-tab-bar__item--active' : ''}`}
      onClick={() => onTabChange('my-songs')}
    >
      <img src={MY_SONGS_ICON} alt="" width={24} height={24} />
      <span>My Songs</span>
    </div>
  </div>
);

const MainLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useUserStore();
  const fetchGenres = useGenreStore((state) => state.fetchGenres);
  const fetchMoods = useMoodStore((state) => state.fetchMoods);

  // 进入 Dashboard 布局时加载 genre 数据
  useEffect(() => {
    fetchGenres();
    fetchMoods();
  }, [fetchGenres, fetchMoods]);

  const isActive = (path: string) => location.pathname.startsWith(path);
  
  const getActiveTab = () => {
    if (location.pathname.includes('/my-songs')) return 'my-songs';
    return 'dashboard';
  };

  const handleTabChange = (tab: string) => {
    if (tab === 'dashboard') navigate(ROUTES.dashboard);
    if (tab === 'my-songs') navigate(ROUTES.mySongs);
  };

  // 底部 Tab 只在 Dashboard、MySongs、MyAlbums 页面显示
  const showMobileTabBar = 
    location.pathname === '/app/dashboard' ||
    location.pathname === '/app/my-songs' ||
    location.pathname === '/app/my-songs/albums';

  const handleLogout = () => {
    logout();
    window.location.href = ROUTES.login;
  };

  // 移动端顶部 Header 只在 Dashboard、MySongs、MyAlbums 页面显示
  const showMobileHeader = 
    location.pathname === '/app/dashboard' ||
    location.pathname === '/app/my-songs' ||
    location.pathname === '/app/my-songs/albums';

  return (
    <div className={`main-layout ${showMobileHeader ? 'main-layout--with-mobile-header' : ''}`}>
      {/* 移动端顶部导航 - 固定在顶部 */}
      {showMobileHeader && (
        <MobileHeader user={user} onNavigate={navigate} onLogout={handleLogout} />
      )}

      {/* 侧边栏 - 桌面端 */}
      <aside className="sidebar">
        <Logo />
        
        {/* 用户信息 */}
        <div className="sidebar__user">
          <Avatar src={user?.avatar} size="large" />
          <div className="sidebar__user-name">{user?.name || 'Artist Name'}</div>
          {/* <div 
            className="sidebar__edit-profile"
            onClick={() => navigate(ROUTES.editProfile)}
          >
            <EditIcon />
            <span>Edit Profile</span>
          </div> */}
        </div>

        <div className="sidebar__divider" />

        {/* Release Song 按钮 */}
        <div className="sidebar__release-btn">
          <Button 
            type="primary"
            className="release-btn"
            onClick={() => navigate(ROUTES.releaseType)}
          >
            <img src={UPLOAD_ICON} alt="" width={24} height={24} />
            <span>Release Song</span>
          </Button>
        </div>

        {/* 导航菜单 */}
        <nav className="sidebar__nav">
          <NavItem 
            icon={<img src={HOME_ICON} alt="" width={24} height={24} />} 
            label="Dashboard" 
            active={isActive('/app/dashboard')}
            onClick={() => navigate(ROUTES.dashboard)}
          />
          <NavItem 
            icon={<img src={MY_SONGS_ICON} alt="" width={24} height={24} />} 
            label="My Songs" 
            active={isActive('/app/my-songs')}
            onClick={() => navigate(ROUTES.mySongs)}
          />
        </nav>
      </aside>

      {/* 主内容区 */}
      <main className="main-content">
        {/* 右上角头像 */}
        <div className="main-content__header">
          <UserAvatarDropdown 
            user={user} 
            onNavigate={navigate}
            onLogout={handleLogout}
          />
        </div>
        
        <Outlet />
      </main>

      {/* 移动端底部导航 - 只在 Dashboard/MySongs/MyAlbums 显示 */}
      {showMobileTabBar && (
        <MobileTabBar activeTab={getActiveTab()} onTabChange={handleTabChange} />
      )}
    </div>
  );
};

export default MainLayout;
